import {
  Avatar,
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Input,
  type Selection,
  Snippet,
  type SortDescriptor,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  useDisclosure,
} from "@heroui/react";
import { useStore } from "@nanostores/react";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronDownIcon, FilterIcon, RefreshCw, SearchIcon } from "lucide-react";
import { atom } from "nanostores";
import { type Key, useCallback, useMemo, useRef, useState } from "react";
import * as R from "remeda";
import type { Address } from "viem";
import { TokenProposalStatus } from "../../queries/tokenReg";
import { usePaginatedItems } from "../../hooks/usePaginatedItems";
import { type TokenDetails, usePortalTokenList, useRefreshTokenDetails, useTokenDetailsList } from "../../queries/tokenReg";
import { BottomContent } from "../table/BottomContent";
import { TokenRegDetailsModal } from "./TokenRegDetailsModal";
import { TokenRegistrationProgress } from "./TokenRegStatus";

const ProposalStates = Object.keys(TokenProposalStatus).filter((k) =>
  Number.isNaN(Number(k)),
) as (keyof typeof TokenProposalStatus)[];

const statusTextMap: Record<TokenProposalStatus, string> = {
  [TokenProposalStatus.UNKNOWN]: "Unknown",
  [TokenProposalStatus.PROPOSED]: "Proposed",
  [TokenProposalStatus.ACCEPTED]: "Accepted",
  [TokenProposalStatus.REJECTED]: "Rejected",
} as const;

const $statusFilter = atom<Selection>("all");
const $searchValue = atom<string>("");
const $currentPage = atom<number>(1);

function setStatusFilter(status: Selection) {
  $statusFilter.set(status);
  $currentPage.set(1);
}

function setSearchValue(value: string) {
  $searchValue.set(value);
  $currentPage.set(1);
}

function setCurrentPage(page: number) {
  $currentPage.set(page);
}

function TopContent({ refetch, isPending }: { refetch: () => void; isPending: boolean }) {
  const searchValue = useStore($searchValue);
  const statusFilter = useStore($statusFilter);

  return (
    <div className="flex gap-2">
      <div>
        <Input
          variant="flat"
          radius="full"
          isClearable
          placeholder="Search..."
          startContent={<SearchIcon />}
          value={searchValue}
          onClear={() => setSearchValue("")}
          onValueChange={setSearchValue}
          classNames={{
            inputWrapper: "group-data-[hover=true]:bg-[var(--turnstile-input-hover-color)]",
          }}
        />
      </div>
      <div className="flex-1" />
      <Button isIconOnly variant="light" onPress={refetch} isDisabled={isPending} aria-label="Refresh token list">
        <RefreshCw className={`h-4 w-4 ${isPending ? "animate-spin" : ""}`} />
      </Button>
      <Dropdown
        classNames={{
          content: "turnstile-card-border",
        }}
      >
        <DropdownTrigger>
          <Button
            variant="bordered"
            color="primary"
            radius="full"
            startContent={<FilterIcon size={16} />}
            endContent={<ChevronDownIcon size={16} />}
          >
            Filter
          </Button>
        </DropdownTrigger>
        <DropdownMenu
          aria-label="Proposal status filter"
          closeOnSelect={false}
          selectedKeys={statusFilter}
          selectionMode="multiple"
          onSelectionChange={setStatusFilter}
        >
          {ProposalStates.map((status) => (
            <DropdownItem key={status}>
              {statusTextMap[TokenProposalStatus[status as keyof typeof TokenProposalStatus]]}
            </DropdownItem>
          ))}
        </DropdownMenu>
      </Dropdown>
    </div>
  );
}

export function TokeRegTable() {
  const tokenDetailsModalDisclosure = useDisclosure();
  const tokenDataRef = useRef<TokenDetails | null>(null);
  const queryClient = useQueryClient();

  // Get token list first
  const { data: tokenList, isPending: tokenListPending, refetch: refetchTokenList } = usePortalTokenList();

  const statusFilter = useStore($statusFilter);
  const searchValue = useStore($searchValue);
  const currentPage = useStore($currentPage);

  const [sortDesc, setSortDesc] = useState<SortDescriptor | undefined>();
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Filter token addresses by search only (we need all addresses to fetch details properly)
  const searchFilteredAddresses = useMemo(() => {
    if (!tokenList) return [];

    let addresses = [...tokenList];

    if (searchValue.length > 0) {
      const searchString = searchValue.toLowerCase();
      addresses = addresses.filter((address) => address.toLowerCase().includes(searchString));
    }

    return addresses;
  }, [tokenList, searchValue]);

  // Fetch details for all search-filtered tokens (not paginated yet)
  const tokenDetailQueries = useTokenDetailsList(searchFilteredAddresses);

  // Process the query results
  const allTokenDetails = tokenDetailQueries.data;

  // Apply all filters to the fetched details
  const filteredItems = useMemo(() => {
    let items = [...allTokenDetails];

    // Apply search filter on token details (name, symbol, address)
    if (searchValue.length > 0) {
      const searchString = searchValue.toLowerCase();
      items = items.filter((item) => {
        return (
          item.tokenAddress.toLowerCase().includes(searchString) ||
          item.tokenName.toLowerCase().includes(searchString) ||
          item.tokenSymbol.toLowerCase().includes(searchString)
        );
      });
    }

    // Apply status filter
    if (statusFilter !== "all" && Array.from(statusFilter).length !== ProposalStates.length) {
      items = items.filter((item) => {
        return Array.from(statusFilter).includes(TokenProposalStatus[item.l1ProposalStatus]);
      });
    }

    return items;
  }, [allTokenDetails, statusFilter, searchValue]);

  const sortedItems = useMemo(() => {
    if (!filteredItems) return [];
    if (!sortDesc || !sortDesc.column) return filteredItems;

    const sortDir = sortDesc.direction === "descending" ? "desc" : "asc";

    switch (sortDesc.column) {
      case "tokenAddress":
        return R.sortBy(filteredItems, [R.prop("tokenAddress"), sortDir]);
      case "tokenName":
        return R.sortBy(filteredItems, [(item) => item.tokenName || "", sortDir]);
      case "tokenSymbol":
        return R.sortBy(filteredItems, [(item) => item.tokenSymbol || "", sortDir]);
      case "l1ProposalStatus":
        return R.sortBy(filteredItems, [(item) => TokenProposalStatus[item.l1ProposalStatus] || "", sortDir]);
      default:
        return [];
    }
  }, [sortDesc, filteredItems]);

  // Apply pagination to the final sorted and filtered results
  const paginatedItems = usePaginatedItems(sortedItems, currentPage, itemsPerPage);

  const isPending = tokenListPending || tokenDetailQueries.isPending;

  const refetch = useCallback(() => {
    // Invalidate all token-related queries to force fresh data
    queryClient.invalidateQueries({ queryKey: ["all-tokens"] });
    queryClient.invalidateQueries({ queryKey: ["token-details"] });

    // Then refetch
    refetchTokenList();
    tokenDetailQueries.refetch();
  }, [refetchTokenList, tokenDetailQueries, queryClient]);

  const refreshTokenDetails = useRefreshTokenDetails();

  const refetchSingleToken = useCallback(
    async (tokenAddress: Address) => {
      // Use the refresh function that does a direct chain check
      await refreshTokenDetails(tokenAddress);
    },
    [refreshTokenDetails],
  );

  console.debug(`TokenRegTable allTokenDetails: ${JSON.stringify(allTokenDetails)}, isPending: ${isPending}`);

  const renderCell = useCallback(
    (item: TokenDetails, columnKey: Key) => {
      switch (columnKey) {
        case "tokenAddress":
          return (
            <Snippet
              size="sm"
              hideSymbol
              codeString={item.tokenAddress}
              classNames={{
                pre: "text-sm",
                copyButton: "text-sm !min-w-4 !size-4",
              }}
            >
              {item.tokenAddress}
            </Snippet>
          );

        case "tokenName":
          return (
            <div className="flex gap-2 items-center">
              <Avatar name={item.tokenName?.[0]} color="primary" classNames={{ base: "!size-6" }} />
              {item.tokenName}
            </div>
          );

        case "tokenSymbol":
          return item.tokenSymbol ?? "N/A";

        case "status":
          return <TokenRegistrationProgress tokenData={item} />;

        case "details": {
          const isFullyRegistered = item.l1RegistrationStatus && item.l2RegistrationStatus;

          if (!isFullyRegistered && item.l1ProposalStatus === TokenProposalStatus.ACCEPTED) {
            return (
              <div className="flex gap-2">
                <Button
                  color="primary"
                  size="sm"
                  radius="full"
                  onPress={() => {
                    tokenDataRef.current = item;
                    tokenDetailsModalDisclosure.onOpen();
                  }}
                >
                  Complete
                </Button>
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  onPress={() => refetchSingleToken(item.tokenAddress)}
                  isDisabled={isPending}
                >
                  <RefreshCw className={`h-4 w-4 ${isPending ? "animate-spin" : ""}`} />
                </Button>
              </div>
            );
          }

          if (!isFullyRegistered) {
            return (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  radius="full"
                  onPress={() => {
                    tokenDataRef.current = item;
                    tokenDetailsModalDisclosure.onOpen();
                  }}
                >
                  Details
                </Button>
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  onPress={() => refetchSingleToken(item.tokenAddress)}
                  isDisabled={isPending}
                >
                  <RefreshCw className={`h-4 w-4 ${isPending ? "animate-spin" : ""}`} />
                </Button>
              </div>
            );
          }

          return (
            <Button
              size="sm"
              radius="full"
              onPress={() => {
                tokenDataRef.current = item;
                tokenDetailsModalDisclosure.onOpen();
              }}
            >
              Details
            </Button>
          );
        }

        default:
          return "N/A";
      }
    },
    [tokenDetailsModalDisclosure.onOpen, refetchSingleToken, isPending],
  );

  return (
    <>
      <Table
        aria-label="Propose Tokens"
        topContent={<TopContent refetch={refetch} isPending={isPending} />}
        bottomContent={
          <BottomContent
            totalItemSize={filteredItems.length}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            setCurrentPage={setCurrentPage}
            setItemsPerPage={setItemsPerPage}
          />
        }
        sortDescriptor={sortDesc}
        onSortChange={setSortDesc}
        classNames={{
          base: "row-start-2 col-span-12",
          wrapper: "turnstile-card-border",
          th: "text-white hover:!text-white text-sm",
          tbody: "tabular-nums *:border-b",
        }}
      >
        <TableHeader>
          <TableColumn key="tokenName" allowsSorting>
            Token name
          </TableColumn>
          <TableColumn key="tokenSymbol" allowsSorting>
            Symbol
          </TableColumn>
          <TableColumn key="tokenAddress" allowsSorting>
            Address
          </TableColumn>
          <TableColumn key="status">Status</TableColumn>
          <TableColumn key="details"> </TableColumn>
        </TableHeader>
        <TableBody
          isLoading={isPending}
          loadingContent={<Spinner label="Loading..." />}
          emptyContent="No tokens to display."
          items={paginatedItems}
        >
          {(item: TokenDetails) => (
            <TableRow key={item.tokenAddress}>
              {(columnKey) => <TableCell>{renderCell(item, columnKey)}</TableCell>}
            </TableRow>
          )}
        </TableBody>
      </Table>

      <TokenRegDetailsModal
        isOpen={tokenDetailsModalDisclosure.isOpen}
        onClose={() => {
          tokenDetailsModalDisclosure.onClose();
          tokenDataRef.current = null;
        }}
        tokenData={tokenDataRef.current}
      />
    </>
  );
}
